import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Layout from 'antd/lib/layout';
import Menu from 'antd/lib/menu';
import { TabBar } from 'antd-mobile';
import PropTypes from 'prop-types';

// ICONS
import homeLightIcon from './assets/home-light.svg';
import homeDarkIcon from './assets/home-dark.svg';
import homeBlueIcon from './assets/home-blue.svg';
import savingsLightIcon from './assets/savings-light.svg';
import savingsDarkIcon from './assets/savings-dark.svg';
import savingsBlueIcon from './assets/savings-blue.svg';
import monitoringLightIcon from './assets/monitoring-light.svg';
import monitoringDarkIcon from './assets/monitoring-dark.svg';
import monitoringBlueIcon from './assets/monitoring-blue.svg';
import alertsLightIcon from './assets/alerts-light.svg';
import alertsDarkIcon from './assets/alerts-dark.svg';
import alertsBlueIcon from './assets/alerts-blue.svg';
import helpLightIcon from './assets/help-light.svg';
import helpDarkIcon from './assets/help-dark.svg';
import helpBlueIcon from './assets/help-blue.svg';
import { HeaderComponent } from '../Header';
import { HamburgerIcon } from './components/HamburgerIcon';

// IMAGES
import logo from '../../images/logo.png';
import darkModeLogo from '../../images/logo-light.png';

import './PageWrapper.less';

const mainStyle = {
  height: '100%',
};

const menuStyle = {
  position: 'absolute',
  justifyContent: 'flex-start',
};

const lowBrightnessStyle = {
  filter: 'brightness(0.5)',
  backdropFilter: 'brightness(1)',
};

export const PageWrapper = (props) => {
  const { Sider, Content } = Layout;
  const { children, category, buildings, selectedBuildingName, theme, changeTheme } = props;
  const history = useHistory();
  const [selectedCategory, setSelectedCategory] = useState('');
  const [siderCollapsed, setSiderCollapsed] = useState(true);
  const [permissions, setPermissions] = useState([]);
  const { building } = useParams();

  // This function is called to set a building in the session if going to the app using a link
  const storeBuildingInSession = () => {
    const selectedBuildings = buildings.find((buildingObj) => buildingObj.identifier === building);
    if (selectedBuildings) {
      sessionStorage.setItem('selectedBuildingName', selectedBuildings.name);
    }
    const selectedBuildingFromSession = sessionStorage.getItem('selectedBuilding');
    if (!selectedBuildingFromSession) sessionStorage.setItem('selectedBuilding', building);
  };

  const getBuildingPermissions = () => {
    const selectedBuildingFromSession = sessionStorage.getItem('selectedBuilding');
    const selectedBuildingObj = buildings.find((buildingObj) => buildingObj.identifier === selectedBuildingFromSession);
    setPermissions(selectedBuildingObj.permissions);
  };

  useEffect(() => {
    function confirmBuildingCode() {
      if (building) {
        let found = false;
        buildings.forEach((singleBuilding) => {
          if (singleBuilding.identifier === building) {
            found = true;
          }
        });
        if (!found) {
          history.push('/404');
        }
      }
    }

    async function setData() {
      setSelectedCategory(category);
    }

    storeBuildingInSession();
    getBuildingPermissions();
    confirmBuildingCode();
    setData();
  }, [category, building]);

  const buildingId = building || sessionStorage.getItem('selectedBuilding');

  const collapseSider = () => {
    setSiderCollapsed(!siderCollapsed);
  };

  const getContentStyle = () => {
    return siderCollapsed ? {} : lowBrightnessStyle;
  };

  return (
    <Layout className={`app-layout-${theme}`} style={mainStyle}>
      <Sider
        width={237}
        collapsible
        collapsed={siderCollapsed}
        onCollapse={collapseSider}
        className="app-menu"
        style={menuStyle}
        trigger={<HamburgerIcon theme={theme} />}
        theme={theme}
      >
        <a href="/">
          {theme === 'light' ? (
            <img className="app-sidebar__parity-logo" src={logo} alt="Parity Logo" />
          ) : (
            <img className="app-sidebar__parity-logo" src={darkModeLogo} alt="Parity Logo" />
          )}
        </a>
        <Menu className="app-sidebar-top-menu" mode="inline" theme={theme} selectedKeys={[selectedCategory]}>
          <Menu.Item
            key="home"
            title={null}
            onClick={() => {
              history.push(`/home/${buildingId}/`);
            }}
          >
            {selectedCategory === 'home' ? (
              <img src={homeBlueIcon} alt="selected home icon" className="app-sidebar__icon" />
            ) : (
              <img
                src={theme === 'light' ? homeLightIcon : homeDarkIcon}
                alt="home icon"
                className="app-sidebar__icon"
              />
            )}
            <span className="app-menu-item-name">Home</span>
          </Menu.Item>
          {permissions.indexOf('savings') !== -1 && (
            <Menu.Item
              key="savings"
              title={null}
              onClick={() => {
                history.push(`/savings/${buildingId}/`);
              }}
            >
              {selectedCategory === 'savings' ? (
                <img src={savingsBlueIcon} alt="selected savings icon" className="app-sidebar__icon" />
              ) : (
                <img
                  src={theme === 'light' ? savingsLightIcon : savingsDarkIcon}
                  alt="savings icon"
                  className="app-sidebar__icon"
                />
              )}
              <span className="app-menu-item-name">Savings</span>
            </Menu.Item>
          )}
          {permissions.indexOf('monitoring') !== -1 && (
            <Menu.Item
              key="monitoring"
              title={null}
              onClick={() => {
                sessionStorage.setItem('customDataPoints', JSON.stringify([]));
                history.push(`/monitoring/${buildingId}/`);
              }}
            >
              {selectedCategory === 'monitoring' ? (
                <img src={monitoringBlueIcon} alt="selected monitoring icon" className="app-sidebar__icon" />
              ) : (
                <img
                  src={theme === 'light' ? monitoringLightIcon : monitoringDarkIcon}
                  alt="monitoring icon"
                  className="app-sidebar__icon"
                />
              )}
              <span className="app-menu-item-name">Monitoring</span>
            </Menu.Item>
          )}
          {permissions.indexOf('alerts') !== -1 && (
            <Menu.Item
              key="alerts"
              title={null}
              onClick={() => {
                history.push(`/alerts/${buildingId}/`);
              }}
            >
              {selectedCategory === 'alerts' ? (
                <img src={alertsBlueIcon} alt="selected alerts icon" className="app-sidebar__icon" />
              ) : (
                <img
                  src={theme === 'light' ? alertsLightIcon : alertsDarkIcon}
                  alt="alerts icon"
                  className="app-sidebar__icon"
                />
              )}
              <span className="app-menu-item-name">Alerts</span>
            </Menu.Item>
          )}
        </Menu>
        <Menu className="app-sidebar-bottom-menu" mode="inline" theme={theme} selectedKeys={[selectedCategory]}>
          <Menu.Item key="help" title={null} onClick={() => history.push('/help/')}>
            {selectedCategory === 'help' ? (
              <img src={helpBlueIcon} alt="selected help icon" />
            ) : (
              <img src={theme === 'light' ? helpLightIcon : helpDarkIcon} alt="help icon" />
            )}
            <span className="app-menu-item-name">Help</span>
          </Menu.Item>
        </Menu>
      </Sider>
      <Content style={getContentStyle()} className="page-content">
        <HeaderComponent
          buildings={buildings}
          selectedBuildingName={selectedBuildingName}
          theme={theme}
          changeTheme={changeTheme}
        />
        <Layout>
          <Content>{children}</Content>
        </Layout>
      </Content>
      <TabBar prerenderingSiblingsNumber={4} hidden={false}>
        <div
          onClick={() => {
            history.push(`/home/${buildingId}/`);
          }}
          onKeyDown={() => {
            history.push(`/home/${buildingId}/`);
          }}
          role="tab"
          tabIndex={0}
        >
          <TabBar.Item key="home">
            {selectedCategory === 'home' ? (
              <img src={homeBlueIcon} alt="selected home icon" />
            ) : (
              <img className="menu-icon-img" src={theme === 'light' ? homeLightIcon : homeDarkIcon} alt="home icon" />
            )}
            <span className="tab-bar-item-name">Home</span>
          </TabBar.Item>
        </div>
        <div
          onClick={() => {
            history.push(`/savings/${buildingId}/`);
          }}
          onKeyDown={() => {
            history.push(`/savings/${buildingId}/`);
          }}
          role="tab"
          tabIndex={-1}
        >
          <TabBar.Item key="savings">
            {selectedCategory === 'savings' ? (
              <img src={savingsBlueIcon} alt="selected savings icon" />
            ) : (
              <img
                className="menu-icon-img"
                src={theme === 'light' ? savingsLightIcon : savingsDarkIcon}
                alt="savings icon"
              />
            )}
            <span className="tab-bar-item-name">Savings</span>
          </TabBar.Item>
        </div>
        <div
          onClick={() => {
            history.push(`/monitoring/${buildingId}/`);
          }}
          onKeyDown={() => {
            history.push(`/monitoring/${buildingId}/`);
          }}
          role="tab"
          tabIndex={-2}
        >
          <TabBar.Item key="monitoring">
            {selectedCategory === 'monitoring' ? (
              <img src={monitoringBlueIcon} alt="selected monitoring icon" />
            ) : (
              <img
                className="menu-icon-img"
                src={theme === 'light' ? monitoringLightIcon : monitoringDarkIcon}
                alt="monitoring icon"
              />
            )}
            <span className="tab-bar-item-name">Monitoring</span>
          </TabBar.Item>
        </div>
        <div
          onClick={() => {
            history.push(`/alerts/${buildingId}/`);
          }}
          onKeyDown={() => {
            history.push(`/alerts/${buildingId}/`);
          }}
          role="tab"
          tabIndex={-3}
        >
          <TabBar.Item key="alerts">
            {selectedCategory === 'alerts' ? (
              <img src={alertsBlueIcon} alt="selected alerts icon" />
            ) : (
              <img
                className="menu-icon-img"
                src={theme === 'light' ? alertsLightIcon : alertsDarkIcon}
                alt="alerts icon"
              />
            )}
            <span className="tab-bar-item-name">Alerts</span>
          </TabBar.Item>
        </div>
      </TabBar>
    </Layout>
  );
};

PageWrapper.propTypes = {
  category: PropTypes.string.isRequired,
  children: PropTypes.any.isRequired,
};
